import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/coastal-dusk.css';

const WelcomePage = () => {
  const navigate = useNavigate();
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [hasPaidAccess, setHasPaidAccess] = useState(null);
  const musicRef = useRef(null);
  const voiceRef = useRef(null);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setHasPaidAccess(false);
          return;
        }
        
        const response = await axios.get(
          `${BACKEND_URL}/api/interpretation/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setHasPaidAccess(response.data.has_paid_access);
      } catch (err) {
        console.error('Error checking access:', err);
        setHasPaidAccess(false);
      }
    };
    
    checkAccess();
    
    // Auto-play background music
    const tryAutoPlay = async () => {
      if (musicRef.current) {
        try {
          musicRef.current.volume = 0.5;
          await musicRef.current.play();
          setMusicPlaying(true);
        } catch (err) {
          console.log('Auto-play blocked');
        }
      }
    };
    
    const timer = setTimeout(tryAutoPlay, 300);
    return () => clearTimeout(timer);
  }, [BACKEND_URL]);

  const handleMusicToggle = async () => {
    if (musicRef.current) {
      try {
        if (musicPlaying) {
          musicRef.current.pause();
          setMusicPlaying(false);
        } else {
          await musicRef.current.play();
          setMusicPlaying(true);
        }
      } catch (err) {
        console.error('Error toggling music:', err);
      }
    }
  };

  const handleVoiceToggle = async () => {
    if (voiceRef.current) {
      try {
        if (voicePlaying) {
          voiceRef.current.pause();
          voiceRef.current.currentTime = 0;
          setVoicePlaying(false);
        } else {
          await voiceRef.current.play();
          setVoicePlaying(true);
        }
      } catch (err) {
        console.error('Error toggling voice:', err);
      }
    }
  };

  const handleContinue = async () => {
    let currentPaidAccess = hasPaidAccess;
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(
          `${BACKEND_URL}/api/interpretation/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        currentPaidAccess = response.data.has_paid_access;
      }
    } catch (err) {
      console.error('Error re-checking access:', err);
    }
    
    if (currentPaidAccess) {
      navigate('/privacy-notice');
    } else {
      // Paywall active — route to payment page
      navigate('/payment');
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--grey-deep)' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
      
      {/* Gradient Orbs */}
      <div className="landing-orb cool focus-shift" style={{ '--orb-opacity': '0.45', top: '-5%', left: '-5%', width: '45vw', height: '45vw', maxWidth: '500px', maxHeight: '500px' }} />
      <div className="landing-orb neutral" style={{ '--orb-opacity': '0.3', top: '55%', right: '-5%', width: '42vw', height: '42vw', maxWidth: '450px', maxHeight: '450px', animationDelay: '-4s' }} />

      {/* Audio Elements */}
      <audio
        ref={musicRef}
        preload="auto"
        loop
        onPlay={() => setMusicPlaying(true)}
        onPause={() => setMusicPlaying(false)}
      >
        <source src="/assets/page1/coastal-music.mp3" type="audio/mpeg" />
      </audio>
      <audio
        ref={voiceRef}
        preload="auto"
        onPlay={() => setVoicePlaying(true)}
        onEnded={() => setVoicePlaying(false)}
      >
        <source src="/assets/page1/welcome-voice-new.mp3" type="audio/mpeg" />
      </audio>

      {/* Music Toggle */}
      <button 
        className="music-toggle-coastal"
        onClick={handleMusicToggle}
        data-testid="toggle-music-btn"
      >
        <span>{musicPlaying ? 'Playing' : 'Music'}</span>
      </button>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col items-center justify-center px-6 relative z-10">
        <div className="col text-center">
          <h1 
            className="text-display mb-4"
            style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', lineHeight: 1.2 }}
          >
            Welcome
          </h1>
          <p 
            className="text-body mb-8"
            style={{ fontSize: '0.92rem', maxWidth: '320px', margin: '0 auto 2rem' }}
          >
            Let's settle in.
          </p>

          {/* Play Welcome Voice */}
          <button
            onClick={handleVoiceToggle}
            className="cta-pill-landing mb-4"
            style={{ fontSize: '0.75rem', padding: '10px 28px', minWidth: '160px' }}
            data-testid="toggle-voice-btn"
          >
            {voicePlaying ? 'Pause Welcome' : 'Play Welcome'}
          </button>

          {/* Continue Button */}
          <div>
            <button
              onClick={handleContinue}
              className="cta-pill-landing"
              style={{ fontSize: '0.75rem', padding: '10px 28px', minWidth: '160px' }}
              data-testid="continue-btn"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
